import { useState } from "react";
import { Play } from "lucide-react";

export const MINIGAME_INFO = {
  handling: { title: "Handling · Precision Timing", steps: [
    "A marker sweeps left and right across the bar.",
    "Tap LOCK when the marker is inside the green center.",
    "The closer to center, the higher you score — clear every maneuver.",
  ] },
  trail: { title: "Trail · Obstacle Timing", steps: [
    "A bar fills as your horse approaches each obstacle.",
    "Tap GO when the fill reaches the green zone near the end.",
    "Going too early or too late loses points.",
  ] },
  freestyle: { title: "Freestyle · Rhythm", steps: [
    "The circle pulses on every beat of the music.",
    "Tap TO THE BEAT in time with each pulse.",
    "Build a combo for a bonus — timing is everything.",
  ] },
  haltering: { title: "Haltering · Slip the Loop", steps: [
    "A ring slides back and forth along the bar.",
    "Tap LOCK when it lines up over the nose (green center).",
    "Land five good locks to finish.",
  ] },
  leading: { title: "Leading · Keep Pace", steps: [
    "The pace bar slowly drifts downward.",
    "Tap WALK ON to keep it inside the green band.",
    "Hold a steady pace the whole way to score well.",
  ] },
  feet: { title: "Picking Feet · Tap the Hoof", steps: [
    "One hoof is highlighted each round.",
    "Tap the highlighted hoof — six in a row.",
    "Both speed and accuracy count.",
  ] },
  tying: { title: "Tying · Stay Patient", steps: [
    "Watch your horse — it stays calm, then sometimes pulls back.",
    "Tap EASE OFF only when it PULLS.",
    "Don't fuss while it's calm — that costs points.",
  ] },
  trailer: { title: "Trailer Loading · Step Up", steps: [
    "The ramp light blinks between GREEN and off.",
    "Tap STEP UP only while the light is GREEN.",
    "Stepping on red pushes your horse back down the ramp.",
  ] },
  desensitize: { title: "Desensitizing · Stay Calm", steps: [
    "Scary objects appear at random.",
    "Tap REASSURE as soon as one shows up.",
    "Keep the red spook meter from filling.",
  ] },
  saddling: { title: "Saddling · Easy Does It", steps: [
    "A needle sweeps across the bar.",
    "Tap STEADY when it sits in the green center.",
    "Three gentle steps to finish tacking up.",
  ] },
  first_ride: { title: "First Ride · Find Balance", steps: [
    "The rider sways off the center line.",
    "Tap ◀ / ▶ to counter the sway and stay centered.",
    "Keep balanced through the whole ride.",
  ] },
  wtc: { title: "Walk / Trot / Canter · Cue the Gait", steps: [
    "The judge calls out a gait.",
    "Tap the matching gait button as fast as you can.",
    "Answer before the next call to score the point.",
  ] },
};

export const MinigameGate = ({ gameKey, children }) => {
  const [started, setStarted] = useState(false);
  const info = MINIGAME_INFO[gameKey] || { title: "Get Ready", steps: [] };
  if (started) return children();
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-xl" data-testid="minigame-instructions">
      <p className="font-display text-lg font-bold text-primary" data-testid="minigame-instructions-title">{info.title}</p>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">How to play</p>
      <ol className="mt-2 space-y-1.5">
        {info.steps.map((s, i) => (
          <li key={i} className="flex gap-2 text-sm text-foreground/90">
            <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary/10 text-[11px] font-bold text-primary">{i + 1}</span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-[11px] text-muted-foreground">The timer starts only when you tap Start — take your time reading.</p>
      <button data-testid="minigame-start-btn" onClick={() => setStarted(true)}
        className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-bold text-primary-foreground active:scale-95">
        <Play size={18} /> Start
      </button>
    </div>
  );
};
